import * as React from 'react';
import * as _ from 'lodash';
import * as moment from 'moment';
import * as qs from 'query-string';
import { Dispatch, connect } from 'react-redux';
import styled from 'styled-components';
import { IApplicationState, IAppState, AppActionCreators, IAutomatedJobState, IAutomatedJobListResponse, AutomatedJobActionCreators } from '@nkr/store/index';
import { ICron, ISortBy } from '@nkr/models/index';
import { DisplayType } from '@nkr/enums/index';
import { Table, Glyphicon, ToggleButtonGroup, ToggleButton, FormControl } from 'react-bootstrap';
import { FormControlWrapper } from '@nkr/views/swap-management/swaps/styles';
import { IRefreshInterval } from '@nkr/views/swap-management/swaps/models';

enum OverallSize {
    XS = 1,
    SM = 2,
    MD = 3,
    LG = 4,
    XL = 5,
    GH = 99
}

enum JobStatus {
    Good = 1,
    Late = 2,
    ReallyLate = 3,
    LastRunNotSuccessful = 4
}

const sizes = [
    { value: OverallSize.XS, label: 'XS', fontSize: 10, padding: 2 },
    { value: OverallSize.SM, label: 'SM', fontSize: 12, padding: 3 },
    { value: OverallSize.MD, label: 'MD', fontSize: 14, padding: 5 },
    { value: OverallSize.LG, label: 'LG', fontSize: 16, padding: 7 },
    { value: OverallSize.XL, label: 'XL', fontSize: 18, padding: 9 },
    { value: OverallSize.GH, label: 'GH', fontSize: 8, padding: 0 }
];

interface IJob {
    job: ICron,
    lastRunDateTime: string,
    nextRunDateTime: string,
    lastRunWasSuccessful: boolean,
    remainingTime: string,
    remainingTimeRaw: number,
    include: boolean,
    status: JobStatus
}

const refreshIntervals: IRefreshInterval[] = [
    { value: 0, label: 'Disable' },
    { value: 5, label: '5 seconds' },
    { value: 30, label: '30 seconds' },
    { value: 45, label: '45 seconds' },
    { value: 60, label: '60 seconds' }
];

interface ITableWrapperProps {
    fontSize: number,
    padding: number
}

const TableWrapper = styled(Table) <ITableWrapperProps>`
    & > thead > tr > th,
    & > tbody > tr > td {
        font-size: ${p => p.fontSize}px;
        padding: ${p => p.padding}px;
    }

    & > thead > tr > th {
        cursor: pointer;
        text-align: center;
        padding-bottom: 0px !important;
        border-bottom: 0 none !important;
    }
`;

interface IRowStyleProps {
    backgroundColor: string,
    color: string
}

const RowWrapper = styled.tr<IRowStyleProps>`
    background: ${p => p.backgroundColor} !important;

    & > td {

        color: ${p => p.color};
        text-align: center;
    }
`;

interface IOwnProps {
    isOps: boolean
}

interface IStateProps {
    automatedJobs: IAutomatedJobState,
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getAutomatedJobs: (isOps: boolean) => Promise<IAutomatedJobListResponse>,
    changeDisplay: (display: number) => void
}

type TProps = IStateProps & IDispatchProps & IOwnProps;

interface IState {
    loading: boolean,
    colorCoding: number,
    filter: number,
    group: string,
    autoRefresh: number,
    refresh: number,
    sortBy: ISortBy[],
    overallSize: OverallSize
}

class CronJobs extends React.Component<TProps, IState> {
    readonly state: IState = {
        loading: false,
        colorCoding: 1,
        filter: 1,
        group: '',
        autoRefresh: 0,
        refresh: 0,
        sortBy: [],
        overallSize: OverallSize.MD
    }

    refreshInterval: any;
    countdownInterval: any;

    componentDidMount = () => {
        this.getData();

        if (this.state.autoRefresh > 0) {
            this.countdownInterval = setInterval(() => this.countdownRefresh(), this.state.autoRefresh * 1000);
        }
    }

    getData = () => {
        let parsed = qs.parse(window.location.search);
        let noMenus = parsed['no-menus'] != null ? parsed['no-menus'] == 1 : false;
        let autoRefresh = parsed['refresh'] != null ? parseInt(parsed['refresh']) : 0;

        this.props.showLoading('Loading jobs, one moment please...').then(() => {
            this.props.getAutomatedJobs(this.props.isOps).then(response => {
                this.props.hideLoading();

                this.setState({
                    autoRefresh
                }, () => {
                    clearInterval(this.refreshInterval);
        
                    if (this.state.autoRefresh > 0){
                        this.refreshInterval = setInterval(() => this.refresh(), this.state.autoRefresh * 1000);
                    }

                    if (noMenus) {
                        this.props.changeDisplay(4);
                    }
                });
            });
        });
    }

    getTimeRemaining = (nextRunDateTime: string) => {
        let nrdt = moment(nextRunDateTime);
        let duration = moment.duration(nrdt.diff(moment()));
        let days = Math.abs(duration.days()).toString();
        let hours = Math.abs(duration.hours()).toString();
        let minutes = Math.abs(duration.minutes()).toString();
        let seconds = Math.abs(duration.seconds()).toString();

        days = days.length == 1 ? '0' + days : days;
        hours = hours.length == 1 ? '0' + hours : hours;
        minutes = minutes.length == 1 ? '0' + minutes : minutes;
        seconds = seconds.length == 1 ? '0' + seconds : seconds;

        let remainingTime = '';

        if (duration.days() != 0) {
            remainingTime = `${duration.asSeconds() < 0 ? '+ ' : '- '}${days} D, ${hours} H, ${minutes} M, ${seconds} S`;
        } else {
            remainingTime = `${duration.asSeconds() < 0 ? '+ ' : '- '}${hours} H, ${minutes} M, ${seconds} S`;
        }

        return {
            remainingTime,
            remainingTimeRaw: duration.asSeconds()
        };
    }

    refresh = () => {
        this.setState({
            loading: true
        }, () => {
            this.props.getAutomatedJobs(this.props.isOps).then(response => {
                this.setState({
                    loading: false
                });
            });
        });
    }

    countdownRefresh = () => {
        this.setState({
            refresh: Math.random()
        });
    }

    getJobs = () => {
        let jobs = this.props.automatedJobs.list.filter(item => {
            let filter = this.state.filter == 1 ? true : !item.lastRunWasSuccessful;
            return filter;
        }).map(item => {
            let jobInfo = this.props.automatedJobs.crons.find(x => x.taskId == item.taskId);

            if (jobInfo == null) {
                jobInfo = {
                    id: -1,
                    taskId: item.taskId,
                    description: item.taskId,
                    groupName: 'System',
                    allowManualExecution: false
                };
            }

            let { remainingTime, remainingTimeRaw } = this.getTimeRemaining(item.nextRunDateTime);

            let { lastRunDateTime, nextRunDateTime, lastRunWasSuccessful } = item;

            let isReallyLate = moment(item.nextRunDateTime).add(60, 'minute').isBefore(moment());

            let status = JobStatus.Good;

            if (isReallyLate) {
                status = JobStatus.ReallyLate;
            }

            if (item.lastRunWasSuccessful == false) {
                status = JobStatus.LastRunNotSuccessful;
            }

            let j: IJob = {
                job: jobInfo,
                lastRunDateTime,
                nextRunDateTime,
                lastRunWasSuccessful,
                remainingTime,
                remainingTimeRaw,
                include: jobInfo.groupName == this.state.group || this.state.group == '',
                status
            };

            return j;
        });

        let presortedJobs = _.orderBy(jobs, [item => item.status, item => new Date(item.nextRunDateTime)], ['desc', 'asc']);

        let sortByProperties = this.state.sortBy.map((sortBy) => sortBy.property);
        let sortByDirections = this.state.sortBy.map((sortBy) => sortBy.direction);

        let sortedJobs = this.state.sortBy.length > 0 ? _.orderBy(jobs, sortByProperties, sortByDirections) : presortedJobs;

        return sortedJobs.filter(x => x.include);
    }

    getJobGroups = () => {
        let list = this.props.automatedJobs.crons.map(x => {
            return { name: x.groupName, backgroundColor: '', color: 'black' };
        });

        let uniqueList = _.uniqBy(list, item => item.name);

        return _.sortBy(uniqueList, item => item.name.toUpperCase());
    }

    handleColorCodingChange = (colorCoding: number) => {
        this.setState({
            colorCoding
        });
    }

    handleFilterChange = (filter: number) => {
        this.setState({
            filter
        });
    }

    handleGroupName = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        this.setState({
            group: e.currentTarget.value
        });
    }

    handleDisplayChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        this.props.changeDisplay(parseInt(e.currentTarget.value));
    }

    handleOveralSizeChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        this.setState({
            overallSize: parseInt(e.currentTarget.value)
        });
    }

    handleRefresh = () => {
        this.refresh();
    }

    handleAutoRefreshChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        this.setState({
            autoRefresh: parseInt(e.currentTarget.value)
        }, () => {
            clearInterval(this.refreshInterval);

            if (this.state.autoRefresh > 0)
                this.refreshInterval = setInterval(() => this.refresh(), this.state.autoRefresh * 1000);
        });
    }

    handleSort = (property: string) => {
        let sortBy = _.cloneDeep(this.state.sortBy);
        let sort = sortBy.find(item => item.property == property);

        if (sort != null) {
            if (sort.direction == 'asc') {
                sort.direction = 'desc'
            } else {
                if (sort.direction == 'desc') {
                    sortBy = sortBy.filter(item => item.property != property);
                }
            }
        } else {
            sortBy.push({ property, direction: 'asc' });
        }

        this.setState({
            sortBy
        });
    }

    renderHeaderSort = (property: string) => {
        let sortItem = this.state.sortBy.find(item => item.property == property);

        if (sortItem != null) {
            return <Glyphicon glyph={sortItem.direction == 'asc' ? 'triangle-top' : 'triangle-bottom'} style={{ marginLeft: 5 }} />;
        } else {
            return null;
        }
    }

    render = () => {
        const jobs = this.getJobs();
        const marginTop = this.props.app.display == DisplayType.NoMenuNoTabs ? 10 : 0;

        const jobGroups = this.getJobGroups();
        const overallStyle = sizes.find(x => x.value == this.state.overallSize);

        return (
            <>
                <div className='container-fluid'>
                    <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', height: 30, marginBottom: 20, marginTop }}>
                        <div style={{ flexBasis: '33%', fontSize: 28, fontWeight: 500, display: 'flex', alignItems: 'center' }}>
                            AUTOMATED JOBS {this.state.loading ? <span style={{ fontSize: 12, fontWeight: 400, paddingLeft: 30 }}>fetching jobs...</span> : ''}
                        </div>
                        <div style={{ flexBasis: '33%', display: 'flex', justifyContent: 'center' }}>
                            <div style={{ backgroundColor: 'darkred', color: 'white', padding: '3px 5px', borderRadius: 4 }}>Not Successful</div>&nbsp;
                            <div style={{ backgroundColor: 'firebrick', color: 'white', padding: '3px 5px', borderRadius: 4 }}>Late (&gt; 60 mins)</div>&nbsp;
                            {/* <div style={{ backgroundColor: 'orange', color: 'white', padding: '3px 15px', borderRadius: 4 }}>Late</div>&nbsp; */}
                            <div style={{ backgroundColor: '#133666', color: 'white', padding: '3px 5px', borderRadius: 4 }}>Pending</div>
                        </div>
                        <div style={{ flexBasis: '33%', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                            <div style={{ marginRight: 5, fontSize: 12 }}>Group:</div>
                            <div style={{ marginRight: 20 }}>
                                <FormControl
                                    componentClass='select'
                                    bsSize='xs'
                                    value={this.state.group}
                                    onChange={this.handleGroupName}
                                >
                                    <option value={''}>- All Groups -</option>
                                    {jobGroups.map(item =>
                                        <option value={item.name}>{item.name}</option>
                                    )}
                                </FormControl>
                            </div>

                            <div style={{ marginRight: 5, fontSize: 12 }}>Filter:</div>
                            <div>
                                <ToggleButtonGroup type='radio' name='swaps' value={this.state.filter}>
                                    <ToggleButton type='radio' value={1} className={this.state.filter == 1 ? 'btn-primary btn-xs' : 'btn-xs'} onClick={() => this.handleFilterChange(1)}>All Jobs</ToggleButton>
                                    <ToggleButton type='radio' value={2} className={this.state.filter == 2 ? 'btn-primary btn-xs' : 'btn-xs'} onClick={() => this.handleFilterChange(2)}>Problem Jobs</ToggleButton>
                                </ToggleButtonGroup>
                            </div>
                        </div>
                    </div>

                    <TableWrapper striped condensed fontSize={overallStyle.fontSize} padding={overallStyle.padding} style={{ marginBottom: 10 }}>
                        <thead>
                            <tr>
                                <th style={{ width: '10%' }} onClick={() => this.handleSort('job.group')}>Group {this.renderHeaderSort('job.groupName')}</th>
                                <th style={{ width: '33%' }} onClick={() => this.handleSort('job.description')}>Job {this.renderHeaderSort('job.description')}</th>
                                <th style={{ width: '15%' }} onClick={() => this.handleSort('lastRunDateTime')}>Last Run<br />Date/Time {this.renderHeaderSort('lastRunDateTime')}</th>
                                <th style={{ width: '10%' }} onClick={() => this.handleSort('lastRunDateSuccessul')}>Last Run<br />Successful {this.renderHeaderSort('lastRunDateSuccessul')}</th>
                                <th style={{ width: '15%' }} onClick={() => this.handleSort('nextRunDateTime')}>Next Run<br />Date/Time {this.renderHeaderSort('nextRunDateTime')}</th>
                                <th style={{ width: '17%' }} onClick={() => this.handleSort('remainingTimeRaw')}>Remaining Time<br />Until Next Run {this.renderHeaderSort('remainingTimeRaw')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {jobs.map((item, index) => {
                                let backgroundColor = '#133666';
                                let color = 'white';

                                switch (item.status) {
                                    case JobStatus.ReallyLate:
                                        backgroundColor = 'firebrick';
                                        break;
                                    case JobStatus.Late:
                                        backgroundColor = 'orange';
                                        break;
                                    case JobStatus.LastRunNotSuccessful:
                                        backgroundColor = 'darkred';
                                        break;
                                }

                                if (item.status == JobStatus.LastRunNotSuccessful) {
                                    backgroundColor = 'darkred';
                                }

                                if (this.state.colorCoding == 1)
                                    return (
                                        <RowWrapper key={index} backgroundColor={backgroundColor} color={color}>
                                            <td>{item.job.groupName}</td>
                                            <td>{item.job.description}</td>
                                            <td>{item.lastRunDateTime}</td>
                                            <td>{item.lastRunWasSuccessful ? 'Yes' : 'No'}</td>
                                            <td>{item.nextRunDateTime}</td>
                                            <td style={{ textAlign: 'center' }}>{item.remainingTime}</td>
                                        </RowWrapper>
                                    );
                                else
                                    return (
                                        <tr>
                                            <td>{item.job.groupName}</td>
                                            <td>{item.job.description}</td>
                                            <td>{item.lastRunDateTime}</td>
                                            <td>{item.lastRunWasSuccessful ? 'Yes' : 'No'}</td>
                                            <td>{item.nextRunDateTime}</td>
                                            <td style={{ textAlign: 'center' }}>{item.remainingTime}</td>
                                        </tr>
                                    );
                            })}
                        </tbody>
                    </TableWrapper>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                        <div style={{ fontSize: 12 }}>
                            {jobs.length} out of {this.props.automatedJobs.list.length} jobs shown ...
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div style={{ marginRight: 5, fontSize: 12 }}>Show:</div>
                            <div style={{ marginRight: 10 }}>
                                <FormControlWrapper
                                    value={this.props.app.display}
                                    componentClass='select'
                                    style={{ fontSize: 12 }}
                                    onChange={this.handleDisplayChange}
                                >
                                    <option value='1'>Menus</option>
                                    <option value='4'>No Menus</option>
                                </FormControlWrapper>
                            </div>

                            <div style={{ marginRight: 5, fontSize: 12 }}>Size:</div>
                            <div style={{ marginRight: 10 }}>
                                <FormControlWrapper
                                    value={this.state.overallSize}
                                    componentClass='select'
                                    style={{ fontSize: 12 }}
                                    onChange={this.handleOveralSizeChange}
                                >
                                    {sizes.map((size, index) =>
                                        <option key={'size=' + index} value={size.value}>{size.label}</option>
                                    )}
                                </FormControlWrapper>
                            </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div style={{ marginRight: 5, fontSize: 12 }}>Auto refresh:</div>
                            <div style={{ marginRight: 5 }}>
                                <FormControlWrapper
                                    value={this.state.autoRefresh}
                                    componentClass='select'
                                    style={{ fontSize: 12 }}
                                    onChange={this.handleAutoRefreshChange}
                                >
                                    {refreshIntervals.map((interval, index) =>
                                        <option key={'auto-' + index} value={interval.value}>{interval.label}</option>
                                    )}
                                </FormControlWrapper>
                            </div>
                            <div>
                                <Glyphicon glyph='refresh' title='Refresh now' onClick={this.handleRefresh} />
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }
}

const mapStateToProps = (appState: IApplicationState, ownProps: IOwnProps): IStateProps => {
    return {
        automatedJobs: appState.automatedJobs,
        app: appState.app
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getAutomatedJobs: (isOps) => {
            return dispatch(AutomatedJobActionCreators.getAutomatedJobs(isOps));
        },
        changeDisplay: (display) => {
            dispatch(AppActionCreators.changeDisplay(display));
        }
    };
}

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(CronJobs);